// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { mergeClasses } from 'util/commons';
import { TraceDataDetail } from 'types/purchase';
import TraceBoardColumn from './TraceBoardColumn';
import TraceBoardLines from './TraceBoardLines';
import styles from './PurchaseTraceBoard.module.scss';
import {
  buildEdges,
  buildSiteMap,
  buildSuccessorsMap,
  findConnectedSiteIds,
} from 'util/purchaseBoardUtils';

type Props = {
  traceDetail: TraceDataDetail[];
};

/**
 * PurchaseTraceBoard
 *
 * Renders a horizontally-scrollable board of columns (one per TraceDataDetail stage).
 * Clicking a card highlights the connected flow and draws bezier lines between cards.
 *
 * Architecture:
 *  - Zero re-renders on scroll: SVG lines update imperatively via requestAnimationFrame.
 *  - Card refs are stored in a stable Map<siteId, HTMLElement> so TraceBoardLines
 *    always reads the latest DOM positions without needing React state.
 *  - Highlighted cards use `position:sticky` so they stay visible while their
 *    column scrolls. getBoundingClientRect() on a sticky element returns its
 *    actual visual position, so lines follow the card automatically.
 */
const PurchaseTraceBoard: React.FC<Props> = ({ traceDetail }) => {
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  // Stable mutable map – does not trigger re-renders when refs are updated
  const cardRefs = useRef<Map<string, HTMLElement | null>>(new Map());
  const boardRef = useRef<HTMLDivElement>(null);

  const sortedStages = useMemo(
    () =>
      [...traceDetail]
        .sort((a, b) => a.stageIndex - b.stageIndex)
        .filter((stage) => stage.sites.length > 0),
    [traceDetail]
  );

  const { siteMap, successorsMap } = useMemo(
    () => ({
      siteMap: buildSiteMap(sortedStages),
      successorsMap: buildSuccessorsMap(sortedStages),
    }),
    [sortedStages]
  );

  const highlightedSiteIds = useMemo<Set<string>>(() => {
    if (!selectedSiteId) return new Set();
    return findConnectedSiteIds(selectedSiteId, siteMap, successorsMap);
  }, [selectedSiteId, siteMap, successorsMap]);

  const edges = useMemo<[string, string][]>(() => {
    if (!selectedSiteId) return [];
    return buildEdges(highlightedSiteIds, sortedStages);
  }, [highlightedSiteIds, sortedStages, selectedSiteId]);

  const handleCardRef = useCallback(
    (siteId: string, el: HTMLElement | null) => {
      cardRefs.current.set(siteId, el);
    },
    []
  );

  const handleCardClick = useCallback((siteId: string) => {
    setSelectedSiteId((prev) => (prev === siteId ? null : siteId));
  }, []);

  const handleDeselect = useCallback(() => {
    setSelectedSiteId(null);
  }, []);

  const hasSelection = selectedSiteId !== null;

  return (
    <>
      <div
        ref={boardRef}
        className={mergeClasses(styles.board, {
          [styles.boardDimmed]: hasSelection,
        })}
      >
        <div className={styles.columns} onClick={handleDeselect}>
          {sortedStages.map((column, index) => (
            <TraceBoardColumn
              index={index}
              key={index}
              column={column}
              selectedSiteId={selectedSiteId}
              highlightedSiteIds={highlightedSiteIds}
              hasSelection={hasSelection}
              onCardClick={handleCardClick}
              onCardRef={handleCardRef}
            />
          ))}
        </div>
      </div>

      {/* SVG overlay: position:fixed, redraws imperatively on scroll */}
      <TraceBoardLines
        edges={edges}
        cardRefs={cardRefs.current}
        visible={hasSelection}
        containerRef={boardRef}
      />
    </>
  );
};

export default PurchaseTraceBoard;
